import { Text, Alert, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconAlertCircle } from '@tabler/icons-react';
import { formatFileSize, FILE_VALIDATION_CONFIG } from '@/utils/fileValidation';
import { AppScrollArea } from '@/components/ui/AppScrollArea';
import sharedClasses from '@/components/common/styles/ScrollContainerLayout.module.css';
import { useFileUpload } from './useFileUpload';
import { DropZone } from './DropZone';
import { FilePreview } from './FilePreview';
import type { FileUploadProps } from './FileUpload.types';
import styles from './FileUpload.module.css';

export const FileUpload = ({
  files,
  onFilesChange,
  mode = 'multi',
  disabled = false,
  error,
  accept,
  maxSize = FILE_VALIDATION_CONFIG.MAX_FILE_SIZE,
  maxFiles,
  hasExistingFiles = false,
  label,
  loading = false,
  children,
  description,
  formats,
  layout = 'stacked',
}: FileUploadProps) => {
  const { t } = useTranslation();
  const isDisabled = disabled || loading;
  const isPinned = layout === 'pinned';

  const {
    dragOver,
    fileInputRef,
    validationErrors,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleFileInputChange,
    handleRemoveFile,
    handleZoneClick,
    handleKeyDown,
  } = useFileUpload({
    files,
    onFilesChange,
    config: {
      maxFileSize: maxSize,
      maxFileCount: mode === 'single' ? 1 : maxFiles,
      accept,
    },
    mode,
    disabled: isDisabled,
  });

  const hasFiles = files.length > 0;
  const showCompact = hasFiles || hasExistingFiles;

  // Pinned region: stays fixed at the top in `pinned` layout. Includes the
  // label, the (visually hidden) input, the drop zone and any error messages.
  const dropZoneRegion = (
    <>
      {label && (
        <Text size="sm" fw={500} mb="xs">
          {label}
        </Text>
      )}

      <input
        ref={fileInputRef}
        type="file"
        className={styles.hiddenInput}
        onChange={handleFileInputChange}
        accept={accept}
        multiple={mode === 'multi'}
        disabled={isDisabled}
        tabIndex={-1}
      />

      <DropZone
        disabled={isDisabled}
        dragOver={dragOver}
        hasFiles={showCompact}
        mode={mode}
        onClick={handleZoneClick}
        onKeyDown={handleKeyDown}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        description={description}
        formats={formats}
      />

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          className={styles.errorMessage}
        >
          {error}
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Text size="xs" c="var(--mantine-color-error)" mt={5}>
          {validationErrors.join(', ')}
        </Text>
      )}
    </>
  );

  // Count + total-size summary (multi-select only). Pinned in `pinned` layout.
  const filesSummary = hasFiles && mode === 'multi' && (
    <Group justify="space-between" className={styles.filesSummary}>
      <Text size="sm" fw={500}>
        {t('fileUpload.selectedFiles', {
          count: files.length,
          defaultValue: 'Selected files ({{count}})',
        })}
      </Text>
      <Text size="xs" c="dimmed">
        {formatFileSize(files.reduce((total, file) => total + file.size, 0))}{' '}
        {t('fileUpload.total')}
      </Text>
    </Group>
  );

  // The scrollable list of newly-added files.
  const fileItems = hasFiles && (
    <div className={styles.fileItems}>
      {files.map((file, index) => (
        <FilePreview
          key={`${file.name}-${index}`}
          file={file}
          onRemove={() => handleRemoveFile(index)}
          disabled={isDisabled}
        />
      ))}
    </div>
  );

  // Pinned: drop zone + selected-files summary stay put; only the file items
  // and `children` (existing attachments) scroll.
  if (isPinned) {
    return (
      <div
        className={`${styles.container} ${sharedClasses.scrollContainer} ${error ? styles.error : ''}`}
      >
        <div className={`${sharedClasses.header} ${styles.pinnedHeader}`}>
          {dropZoneRegion}
          {filesSummary}
        </div>
        <AppScrollArea fill fullBleedGutter="md">
          {fileItems}
          {children}
        </AppScrollArea>
      </div>
    );
  }

  // Stacked (legacy): everything flows vertically; `children` precede the list.
  return (
    <div className={`${styles.container} ${error ? styles.error : ''}`}>
      {dropZoneRegion}
      {children}
      {fileItems && (
        <div className={styles.fileList}>
          {filesSummary}
          {fileItems}
        </div>
      )}
    </div>
  );
};
